// src/components/transaction-list.jsx
import React, { useMemo, useState } from 'react';
import moment from 'moment';
import { getTransactionPdf, refundTransaction } from '../services/api.service';
import Helper from '../services/helper.service';
import { FaEllipsisH } from 'react-icons/fa';
import { useConfirm } from './confirm-dialog';

const TYPE_META = {
    'payment-link': { label: 'Lien de paiement', badge: 'bg-purple-100 text-purple-800', icon: '🔗' },
    'services': { label: 'Services', badge: 'bg-blue-100 text-blue-800', icon: '🧾' },
    'avoir': { label: 'Avoir', badge: 'bg-red-100 text-red-800', icon: '📄' },
};

const STATUS_META = {
    'paid': { label: 'Payée', badge: 'bg-green-100 text-green-800', icon: '✔️' },
    'partially-refunded': { label: 'Partiellement remboursée', badge: 'bg-orange-100 text-orange-800', icon: '🌓' },
    'refunded': { label: 'Remboursée', badge: 'bg-blue-100 text-blue-800', icon: '↩️' },
    'refund': { label: 'Avoir', badge: 'bg-yellow-100 text-yellow-800', icon: '📜' },
};

const humanize = (s) => s.replace(/[-_]+/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
const renderTypeBadge = (type) => {
    if (!type) return <span className="text-gray-400">—</span>;
    const meta = TYPE_META[type];
    const label = meta?.label ?? humanize(type);
    const badge = meta?.badge ?? 'bg-gray-100 text-gray-700';
    const icon = meta?.icon ?? 'ℹ️';
    return (
        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${badge}`}>
            <span aria-hidden>{icon}</span>{label}
        </span>
    );
};
const renderStatusBadge = (status) => {
    if (!status) return <span className="text-gray-400">—</span>;
    const meta = STATUS_META[status];
    const label = meta?.label ?? humanize(status);
    const badge = meta?.badge ?? 'bg-gray-100 text-gray-700';
    const icon = meta?.icon ?? 'ℹ️';
    return (
        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${badge}`}>
            <span aria-hidden>{icon}</span>{label}
        </span>
    );
};

/* ---------- Society helpers (deterministic color per society) ---------- */
const getSocietyNameFromTx = (t) => {
    if (t?.societyName) return t.societyName;
    if (t?.society?.name) return t.society.name;
    if (t?.societyId && typeof t.societyId === 'object' && t.societyId.name) return t.societyId.name;
    if (t?.subdomain) return t.subdomain; // last-ditch (unlikely needed now that we show subdomain separately)
    if (typeof t?.societyId === 'string' && t.societyId) return `#${t.societyId.slice(-6)}`;
    return '';
};

// simple djb2 hash → hue
const hashHue = (str) => {
    let h = 5381;
    for (let i = 0; i < str.length; i++) h = ((h << 5) + h) + str.charCodeAt(i);
    return (h >>> 0) % 360;
};

const societyBadgeStyle = (name) => {
    const base = name?.toString().toLowerCase() || 'default';
    const hue = hashHue(base);
    const bg = `hsl(${hue}, 90%, 95%)`;
    const fg = `hsl(${hue}, 40%, 25%)`;
    const border = `hsl(${hue}, 60%, 85%)`;
    return { backgroundColor: bg, color: fg, border: `1px solid ${border}` };
};

const renderSocietyBadge = (name) => {
    if (!name) return null;
    const style = societyBadgeStyle(name);
    return (
        <span
            className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium"
            style={style}
            title={name}
        >
            <span aria-hidden>🏢</span>{name}
        </span>
    );
};
/* ---------------------------------------------------------------------- */

/* ---------- Subdomain helpers (neutral pill) ---------- */
const getSubdomainFromTx = (t) => {
    // stored as string in your Transaction schema
    if (typeof t?.subdomain === 'string' && t.subdomain.trim()) return t.subdomain.trim();
    // if ever populated differently in the future:
    if (t?.subdomain?.subdomain) return String(t.subdomain.subdomain);
    return '';
};

const renderSubdomainBadge = (sub) => {
    if (!sub) return null;
    return (
        <span
            className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200"
            title={sub}
        >
            <span aria-hidden>🌐</span>{sub}
        </span>
    );
};
/* ------------------------------------------------------ */

const TransactionList = ({ transactions, onRowClick, onRefundSuccess }) => {
    const [typeFilter, setTypeFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [openMenuId, setOpenMenuId] = useState(null);
    const [refundTarget, setRefundTarget] = useState(null);
    const confirm = useConfirm();

    const visible = useMemo(() => {
        return (transactions || []).filter(t => {
            const typeMatch = !typeFilter || (t.type || '') === typeFilter;
            const statusMatch = !statusFilter || (t.status || '') === statusFilter;
            return typeMatch && statusMatch;
        });
    }, [transactions, typeFilter, statusFilter]);

    const handleDownloadPDF = async (id) => {
        try {
            await getTransactionPdf(id);
        } catch (err) {
            console.error('Erreur lors du téléchargement du PDF :', err);
        }
    };

    const openRefundModal = (t) => { setOpenMenuId(null); setRefundTarget(t); };
    const closeRefundModal = () => setRefundTarget(null);

    const handleRefundSubmit = async ({ amount, reason, items }) => {
        if (!refundTarget?._id) return;
        const ok = await confirm({
            title: 'Confirmation',
            message: <>Rembourser <strong>{amount.toFixed(2)} €</strong> pour la transaction n° <strong>{refundTarget.billingNumber}</strong> ?</>,
            confirmText: 'Oui',
            cancelText: 'Non',
            tone: 'warning',
        });
        if (!ok) return Helper.toast('Action annulée', 'info');

        try {
            await refundTransaction(refundTarget._id, { amount, reason, items });
            Helper.toast('Remboursement initié', 'success');
            onRefundSuccess?.(refundTarget._id);
            closeRefundModal();
        } catch { }
    };

    if (!transactions?.length) {
        return <p className="text-center text-gray-500 py-12">Aucune transaction trouvée.</p>;
    }

    return (
        <div className="overflow-x-auto bg-white border rounded">
            {/* Toolbar */}
            <div className="flex items-center justify-between px-3 py-2 border-b">
                <div className="text-sm text-gray-500">{visible.length} résultat(s)</div>
                <div className="flex items-center gap-4">
                    {/* Type filter */}
                    <div className="flex items-center gap-2">
                        <label className="text-sm text-gray-700">Type</label>
                        <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="border rounded px-2 py-1 text-sm bg-white">
                            <option value="">Tous</option>
                            <option value="services">Services</option>
                            <option value="payment-link">Lien de paiement</option>
                        </select>
                    </div>

                    {/* Status filter */}
                    <div className="flex items-center gap-2">
                        <label className="text-sm text-gray-700">Statut</label>
                        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="border rounded px-2 py-1 text-sm bg-white">
                            <option value="">Tous</option>
                            <option value="paid">Payée</option>
                            <option value="partially-refunded">Partiellement remboursée</option>
                            <option value="refunded">Remboursée</option>
                            <option value="refund">Avoir</option>
                        </select>
                    </div>
                </div>
            </div>

            <table className="min-w-full text-left">
                <thead className="bg-gray-100">
                    <tr>
                        <th className="p-2">Transaction n° / Société / Sous-domaine</th>
                        <th className="p-2">Nom</th>
                        <th className="p-2">Email</th>
                        <th className="p-2">Créée le</th>
                        <th className="p-2">Type</th>
                        <th className="p-2">Statut</th>
                        <th className="p-2">Montant</th>
                        <th className="p-2"></th>
                    </tr>
                </thead>
                <tbody>
                    {visible.map((t) => {
                        const societyName = getSocietyNameFromTx(t);
                        const subdomain = getSubdomainFromTx(t);
                        return (
                            <tr
                                key={t._id}
                                className="border-b hover:bg-gray-50 cursor-pointer"
                                onClick={() => onRowClick?.(t)}
                            >
                                <td className="p-2">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span>{t.billingNumber}</span>
                                        {renderSocietyBadge(societyName)}
                                        {renderSubdomainBadge(subdomain)}
                                    </div>
                                </td>
                                <td className="p-2">{t.customer?.lastname} {t.customer?.firstname}</td>
                                <td className="p-2">{t.customer?.email || '-'}</td>
                                <td className="p-2">{moment(t.createdAt).format('DD/MM/YYYY')}</td>
                                <td className="p-2">{renderTypeBadge(t.type)}</td>
                                <td className="p-2">{renderStatusBadge(t.status)}</td>
                                <td className="p-2">{Number(t.amount).toFixed(2)} €</td>
                                <td className="p-2" onClick={(e) => e.stopPropagation()}>
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => handleDownloadPDF(t._id)}
                                            className="text-sm bg-primary text-white px-3 py-1 rounded hover:bg-primary-dark"
                                        >
                                            Télécharger
                                        </button>

                                        <div className="relative">
                                            <button
                                                aria-haspopup="menu"
                                                aria-expanded={openMenuId === t._id}
                                                onClick={() => setOpenMenuId(openMenuId === t._id ? null : t._id)}
                                                className="h-8 w-8 inline-flex items-center justify-center rounded border hover:bg-gray-100"
                                                title="Actions"
                                            >
                                                <FaEllipsisH />
                                            </button>

                                            {openMenuId === t._id && (
                                                <div role="menu" className="absolute right-0 mt-2 w-44 rounded-md border bg-white shadow-lg z-10">
                                                    <button
                                                        role="menuitem"
                                                        onClick={() => openRefundModal(t)}
                                                        className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
                                                    >
                                                        Rembourser
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>

            {/* Refund modal (unchanged) */}
            {refundTarget && (
                <RefundModal
                    transaction={refundTarget}
                    onClose={closeRefundModal}
                    onSubmit={handleRefundSubmit}
                />
            )}
        </div>
    );
};

export default TransactionList;

/* -------------------------------------------------------------
 * RefundModal unchanged (kept as in your original)
 * ----------------------------------------------------------- */
const RefundModal = ({ transaction, onClose, onSubmit }) => {
    const lines = useMemo(() => {
        const items = [];
        if (Array.isArray(transaction?.products) && transaction.products.length) {
            transaction.products.forEach((p, i) => {
                items.push({
                    key: `p-${i}`,
                    label: p?.label || 'Produit',
                    qtyMax: Number(p?.quantity || 1),
                    unitPrice: Number(p?.priceTTC || 0),
                    type: 'product',
                    index: i,
                });
            });
        } else if (Array.isArray(transaction?.services) && transaction.services.length) {
            transaction.services.forEach((s, i) => {
                items.push({
                    key: `s-${i}`,
                    label: s?.title?.fr || s?.title?.en || 'Service',
                    qtyMax: 1,
                    unitPrice: Number(s?.price || 0),
                    type: 'service',
                    index: i,
                });
            });
        }
        return items;
    }, [transaction]);

    const [qtyMap, setQtyMap] = useState(() =>
        Object.fromEntries(lines.map(l => [l.key, 0]))
    );
    const [reason, setReason] = useState('');

    const total = useMemo(() => {
        return lines.reduce((sum, l) => sum + (Number(qtyMap[l.key] || 0) * l.unitPrice), 0);
    }, [lines, qtyMap]);

    const canSubmit = total > 0;

    const setQty = (key, next, max) => {
        const v = Math.max(0, Math.min(Number(next || 0), max));
        setQtyMap((m) => ({ ...m, [key]: v }));
    };

    const handleSubmit = () => {
        const items = lines
            .filter(l => Number(qtyMap[l.key]) > 0)
            .map(l => ({
                type: l.type,
                index: l.index,
                label: l.label,
                qty: Number(qtyMap[l.key]),
                unitPrice: l.unitPrice,
                lineTotal: Number(qtyMap[l.key]) * l.unitPrice,
            }));

        onSubmit?.({
            amount: items.reduce((s, it) => s + it.lineTotal, 0),
            reason: reason?.trim() || undefined,
            items,
        });
    };

    return (
        <div className="fixed inset-0 z-20 flex items-center justify-center bg-black/40" onClick={onClose}>
            <div className="w-full max-w-xl rounded-xl bg-white shadow-xl border p-5" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-start justify-between mb-3">
                    <h3 className="text-lg font-semibold">Rembourser la transaction n°{transaction.billingNumber}</h3>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700">✕</button>
                </div>

                {lines.length === 0 ? (
                    <p className="text-sm text-gray-600">Aucun élément remboursable détecté sur cette transaction.</p>
                ) : (
                    <div className="space-y-3 max-h-[60vh] overflow-auto pr-1">
                        {lines.map((l) => (
                            <div key={l.key} className="flex items-center justify-between gap-3 border rounded p-3">
                                <div className="flex-1">
                                    <div className="font-medium text-gray-800">{l.label}</div>
                                    <div className="text-xs text-gray-500">
                                        PU: {l.unitPrice.toFixed(2)} € &middot; Qté max: {l.qtyMax}
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <input
                                        type="number"
                                        min={0}
                                        max={l.qtyMax}
                                        step={1}
                                        value={qtyMap[l.key] ?? 0}
                                        onChange={(e) => setQty(l.key, e.target.value, l.qtyMax)}
                                        className="w-24 border rounded px-2 py-1"
                                    />
                                    <div className="w-24 text-right font-semibold">
                                        {(Number(qtyMap[l.key] || 0) * l.unitPrice).toFixed(2)} €
                                    </div>
                                </div>
                            </div>
                        ))}

                        <div className="pt-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Motif (facultatif)</label>
                            <textarea
                                rows={3}
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                                className="w-full border rounded px-3 py-2"
                                placeholder="Ex: Client a annulé la commande"
                            />
                        </div>
                    </div>
                )}

                <div className="mt-4 flex items-center justify-between">
                    <div className="text-base">
                        Total à rembourser :<br />
                        <span className="font-semibold">{total.toFixed(2)} €</span>
                    </div>
                    <div className="flex gap-2">
                        <button onClick={onClose} className="px-4 py-2 rounded border hover:bg-gray-50">Annuler</button>
                        <button
                            onClick={handleSubmit}
                            disabled={!canSubmit}
                            className={`px-4 py-2 rounded text-white ${canSubmit ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-300 cursor-not-allowed'}`}
                        >
                            Valider le remboursement
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};